import React from "react";
import { Link } from "react-router-dom";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <>
      <nav className="crumb" aria-label="Breadcrumb">
        <ol>
          <li>
            <Link to="/">Home</Link>
          </li>
          <li aria-hidden="true">→</li>
          <li aria-current="page">Privacy Policy</li>
        </ol>
      </nav>

      <div className="legal-hero">
        <div className="eyebrow">LEGAL</div>
        <h1>Privacy Policy</h1>
        <p>Last updated: 25 August 2026</p>
      </div>

      <div className="callout">
        <div className="callout-inner">
          <span className="swatch"></span>
          Fields highlighted like <span className="fill-in">this</span> are placeholders — fill these in with your real contact details before publishing.
        </div>
      </div>

      <div className="legal-wrap">
        <aside className="toc">
          <div className="toc-label">On this page</div>
          <a href="#s1">1. Introduction</a>
          <a href="#s2">2. Scope</a>
          <a href="#s3">3. Information We Collect</a>
          <a href="#s4">4. How We Use Information</a>
          <a href="#s5">5. Legal Basis</a>
          <a href="#s6">6. Communication &amp; Virtual Numbers</a>
          <a href="#s7">7. Call Recording</a>
          <a href="#s8">8. Third-Party Integrations</a>
          <a href="#s9">9. App Permissions</a>
          <a href="#s10">10. Cookies</a>
          <a href="#s11">11. How We Share Information</a>
          <a href="#s12">12. Data Security</a>
          <a href="#s13">13. Data Retention</a>
          <a href="#s14">14. Cross-Border Processing</a>
          <a href="#s15">15. Your Rights</a>
          <a href="#s16">16. Deletion Requests</a>
          <a href="#s17">17. Children's Privacy</a>
          <a href="#s18">18. Customer Responsibilities</a>
          <a href="#s19">19. Third-Party Links</a>
          <a href="#s20">20. Policy Changes</a>
          <a href="#s21">21. Contact Us</a>
          <a href="#s22">22. Grievance Contact</a>
          <a href="#s23">23. Service-Specific Notices</a>
          <a href="#s24">24. Consent</a>
          <a href="#summary">Summary</a>
        </aside>

        <div className="legal-content">
          <section id="s1">
            <h2><span className="num">1.</span> Introduction</h2>
            <p>Shankarshan Technologies Pvt. Ltd. (&ldquo;<strong>Shankarshan</strong>&rdquo;, &ldquo;<strong>Company</strong>&rdquo;, &ldquo;<strong>we</strong>&rdquo;, &ldquo;<strong>us</strong>&rdquo;, or &ldquo;<strong>our</strong>&rdquo;) respects the privacy of individuals and is committed to protecting personal information and other information processed through our products and services.</p>
            <p>This Privacy Policy explains how we collect, use, store, share, protect, and otherwise process information when you access or use our websites, mobile applications, software platforms, communication services, APIs, cloud-based services, customer engagement solutions, and other products or services that link to or refer to this Privacy Policy (collectively, the &ldquo;<strong>Services</strong>&rdquo;).</p>
            <p>By accessing or using our Services, you acknowledge that your information may be processed as described in this Privacy Policy and in accordance with applicable laws.</p>
          </section>

          <section id="s2">
            <h2><span className="num">2.</span> Scope of This Privacy Policy</h2>
            <p>This Privacy Policy applies to all current and future Services provided by Shankarshan Technologies Pvt. Ltd., unless a particular Service is governed by a separate or supplementary privacy policy.</p>
            <p>Our Services may include, without limitation:</p>
            <ul>
              <li>Mobile applications</li>
              <li>Websites and web portals</li>
              <li>Software-as-a-Service (SaaS) platforms</li>
              <li>Cloud communication platforms</li>
              <li>Voice and calling solutions</li>
              <li>Virtual number and business communication services</li>
              <li>Call management and call analytics solutions</li>
              <li>Customer engagement and CRM-related solutions</li>
              <li>WhatsApp Business and messaging integrations</li>
              <li>Chatbots and automation solutions</li>
              <li>APIs, webhooks, and developer services</li>
              <li>Business dashboards and administration portals</li>
              <li>Customer support services</li>
              <li>Any related products, features, integrations, or future services provided by Shankarshan</li>
            </ul>
            <p>This Privacy Policy may also apply to personal information processed by us on behalf of our business customers. In certain circumstances, our business customer may be the primary controller or responsible organization for such information, and the applicable customer agreement or the customer's own privacy policy may also apply.</p>
          </section>

          <section id="s3">
            <h2><span className="num">3.</span> Information We Collect</h2>
            <p>The information we collect depends on the Services you use, how you interact with us, and the features enabled by you or your organization.</p>

            <h3>3.1 Account and Registration Information</h3>
            <p>We may collect information such as:</p>
            <ul>
              <li>Name</li>
              <li>Business or organization name</li>
              <li>User ID</li>
              <li>Username</li>
              <li>Email address</li>
              <li>Mobile or telephone number</li>
              <li>Job title or role</li>
              <li>Login credentials</li>
              <li>Account identifiers</li>
              <li>Organization or tenant identifiers</li>
              <li>Other information provided during registration or account setup</li>
            </ul>

            <h3>3.2 Business and Customer Information</h3>
            <p>When you or your organization use our Services, you may provide or allow us to process information relating to your customers, employees, representatives, agents, or other contacts, including:</p>
            <ul>
              <li>Names</li>
              <li>Telephone or mobile numbers</li>
              <li>Email addresses</li>
              <li>Customer IDs</li>
              <li>Account information</li>
              <li>Communication preferences</li>
              <li>Business contact information</li>
              <li>Notes, tags, or records created within the Services</li>
            </ul>

            <h3>3.3 Communication and Call Information</h3>
            <p>Depending on the Services used, we may collect, receive, generate, or process communication-related information, including:</p>
            <ul>
              <li>Calling number and called number</li>
              <li>Caller and recipient information</li>
              <li>Call date and time</li>
              <li>Call duration</li>
              <li>Call status and logs</li>
              <li>Virtual number information</li>
              <li>Messaging metadata</li>
            </ul>

            <h3>3.6 Technical and Usage Information</h3>
            <p>When you use our Services, we may automatically collect certain technical and usage information, including:</p>
            <ul>
              <li>IP address</li>
              <li>Device type and identifiers</li>
              <li>Operating system</li>
              <li>Browser type and version</li>
              <li>Application version</li>
              <li>Log files</li>
              <li>Error reports</li>
              <li>Feature usage</li>
              <li>Approximate location derived from IP address, where applicable</li>
            </ul>
          </section>

          <section id="s4">
            <h2><span className="num">4.</span> How We Use Information</h2>
            <p>We may use information for the following purposes:</p>
            <ul>
              <li>To provide, improve, and maintain the Services</li>
              <li>To create and manage accounts and authentication</li>
              <li>To route and process calls and messages</li>
              <li>To manage contacts, conversations, and customer interactions</li>
              <li>To detect fraud, abuse, or unauthorized access</li>
              <li>To monitor service performance and fix technical issues</li>
              <li>To communicate with you about your account and updates</li>
              <li>To comply with legal and regulatory obligations</li>
            </ul>
          </section>

          <section id="s5">
            <h2><span className="num">5.</span> Legal Basis</h2>
            <p>We process information where we have an appropriate legal basis or other lawful authority to do so under applicable law, including:</p>
            <ul>
              <li>Your consent, where required</li>
              <li>Performance of a contract</li>
              <li>Compliance with legal obligations</li>
              <li>Legitimate business interests</li>
              <li>Instructions from our business customers when acting on their behalf</li>
            </ul>
          </section>

          <section id="s6">
            <h2><span className="num">6.</span> Communication &amp; Virtual Numbers</h2>
            <p>Certain Shankarshan Services enable businesses and authorized users to manage communications through virtual numbers, communication platforms, calling solutions, and related technologies.</p>
            <p>When these Services are used, we and our authorized service providers may process information necessary to establish and route communications, maintain call records, display analytics, manage virtual numbers, detect fraud and abuse, and meet applicable legal, telecom, and regulatory requirements.</p>
          </section>

          <section id="s7">
            <h2><span className="num">7.</span> Call Recording</h2>
            <p>Where call recording or similar features are enabled through a Service, we may process related audio, metadata, and associated information to provide the feature, support operational use cases, and comply with applicable law and customer instructions.</p>
            <p>Call recording features must be used lawfully and with appropriate notice, consent, and authorization as required by applicable federal, state, or local laws and telecom obligations.</p>
          </section>

          <section id="s8">
            <h2><span className="num">8.</span> Third-Party Integrations</h2>
            <p>Our Services may integrate with third-party platforms, telecom providers, payment processors, CRM systems, messaging APIs, cloud services, and other business tools. These integrations may require us to share or receive data as part of the requested functionality.</p>
            <p>We encourage customers to review the privacy policies of each third-party provider they connect to or use with our Services.</p>
          </section>

          <section id="s9">
            <h2><span className="num">9.</span> App Permissions</h2>
            <p>If you use our mobile or application-based Services, the app may request permissions necessary to provide the requested functionality, such as access to contacts, microphone, notifications, storage, or other device capabilities.</p>
            <p>We do not collect or use device permissions beyond what is necessary for the Service and the permissions you approve.</p>
          </section>

          <section id="s10">
            <h2><span className="num">10.</span> Cookies</h2>
            <p>We may use cookies, web beacons, similar technologies, and analytics tools to remember preferences, understand usage patterns, support security features, and improve our services.</p>
            <p>You may be able to manage or disable cookies through your browser settings, subject to the limitations of doing so.</p>
          </section>

          <section id="s11">
            <h2><span className="num">11.</span> How We Share Information</h2>
            <p>We may share personal information with:</p>
            <ul>
              <li>Business customers and authorized users</li>
              <li>Service providers and vendors who assist with operations, hosting, email, messaging, analytics, support, or security</li>
              <li>Telecom or communication partners necessary to route or deliver services</li>
              <li>Professional advisors and regulators, as required</li>
              <li>Other parties when required by law or necessary to protect the rights or safety of our users, customers, or the Company</li>
            </ul>
            <p>We do not sell personal information for monetary consideration.</p>
          </section>

          <section id="s12">
            <h2><span className="num">12.</span> Data Security</h2>
            <p>We use reasonable administrative, technical, and physical safeguards to protect information from loss, misuse, unauthorized access, disclosure, alteration, or destruction.</p>
            <p>However, no system is completely secure, and we cannot guarantee the absolute security of information transmitted or stored on our systems. You are responsible for keeping your account credentials secure and notifying us promptly of any suspected unauthorized access.</p>
          </section>

          <section id="s13">
            <h2><span className="num">13.</span> Data Retention</h2>
            <p>We retain personal information only for as long as necessary to fulfill the purposes described in this Policy, to meet legal, regulatory, or contractual obligations, or to resolve disputes.</p>
            <p>Retention periods may vary based on the nature of the information and the Service.</p>
          </section>

          <section id="s14">
            <h2><span className="num">14.</span> Cross-Border Processing</h2>
            <p>Some information may be processed outside the country where it was collected, including by service providers, infrastructure providers, or business partners located in other jurisdictions.</p>
            <p>Where applicable, we will use lawful mechanisms to support such transfers and will take reasonable steps to protect information in accordance with applicable data protection laws.</p>
          </section>

          <section id="s15">
            <h2><span className="num">15.</span> Your Rights</h2>
            <p>Depending on applicable law, you may have rights to access, correct, delete, restrict, or object to certain personal information processing, and to request data portability in some circumstances.</p>
            <p>You may also have the right to withdraw consent where consent is the lawful basis for processing.</p>
          </section>

          <section id="s16">
            <h2><span className="num">16.</span> Deletion Requests</h2>
            <p>If you wish to request deletion of information associated with a Service, please contact us using the details below. We will consider your request in accordance with applicable law and the requirements of the relevant Service.</p>
            <p>Some information may be retained where required by law, for legitimate business purposes, or to protect rights, security, or legal claims.</p>
          </section>

          <section id="s17">
            <h2><span className="num">17.</span> Children's Privacy</h2>
            <p>Our Services are not directed to persons under the age of majority, and we do not knowingly collect personal information from children without appropriate parental or lawful consent.</p>
            <p>If you believe a child has provided personal information to us without proper authorization, please contact us so we can take appropriate action.</p>
          </section>

          <section id="s18">
            <h2><span className="num">18.</span> Customer Responsibilities</h2>
            <p>Our customers and authorized users are responsible for ensuring that any personal information they provide or process through our Services is lawful, appropriately authorized, and accompanied by any required notices, consents, and legal basis under applicable law.</p>
            <p>Where you are acting as a business customer or administrator, you are responsible for the appropriate management, notice, and access controls for your users and data.</p>
          </section>

          <section id="s19">
            <h2><span className="num">19.</span> Third-Party Links</h2>
            <p>Our Services may contain links to third-party websites, applications, tools, or resources. We are not responsible for the content, privacy practices, or policies of those third parties.</p>
            <p>We encourage you to review the privacy policies of any third-party site or service you access through our Services.</p>
          </section>

          <section id="s20">
            <h2><span className="num">20.</span> Policy Changes</h2>
            <p>We may update this Privacy Policy from time to time to reflect changes in services, legal requirements, or business practices. Any changes will be posted with a revised effective date.</p>
            <p>Your continued use of the Services after the posting of changes constitutes acceptance of the updated Privacy Policy.</p>
          </section>

          <section id="s21">
            <h2><span className="num">21.</span> Contact Us</h2>
            <div className="info-box">
              <div className="label">Contact</div>
              <p><strong>Shankarshan Technologies Pvt. Ltd.</strong></p>
              <p>Benachity, Durgapur, West Bengal 713213</p>
              <p>Email: <span className="fill-in">info@shankarshan.com</span></p>
              <p>Phone: <span className="fill-in">+91 8800418585</span></p>
            </div>
          </section>

          <section id="s22">
            <h2><span className="num">22.</span> Grievance Contact</h2>
            <p>If you have any complaints or concerns about our handling of personal information, you may contact our designated grievance contact using the details above or by emailing <span className="fill-in">grievance@shankarshan.com</span>.</p>
            <p>We will review and respond to such concerns in a timely manner in accordance with applicable legal requirements.</p>
          </section>

          <section id="s23">
            <h2><span className="num">23.</span> Service-Specific Notices</h2>
            <p>Certain services may have additional or specific privacy notices due to their features, integrations, telecom requirements, legal obligations, or customer configuration. Where such notices apply, they supplement this Privacy Policy.</p>
          </section>

          <section id="s24">
            <h2><span className="num">24.</span> Consent</h2>
            <p>By using our Services or submitting personal information through them, you consent to the collection, processing, storage, and transfer of that information in accordance with this Privacy Policy and applicable law.</p>
            <p>Where consent is required, we will seek it explicitly at the point of collection or through the applicable workflow, configuration, or service contract.</p>
          </section>

          <section id="summary" className="summary-box">
            <h2>Summary</h2>
            <ul>
              <li>We collect only the information needed to provide, secure, and improve our Services.</li>
              <li>We process personal data for service delivery, security, support, legal compliance, and business operations.</li>
              <li>We apply reasonable security and retention practices for the information we hold.</li>
              <li>We respect applicable rights and provide channels for data access, correction, deletion, and concerns.</li>
              <li>We encourage customers and users to review this Privacy Policy and contact us with questions.</li>
            </ul>
          </section>
        </div>
      </div>

      <a href="#top" className="back-to-top" aria-label="Back to top">↑</a>
    </>
  );
};

export default PrivacyPolicy;
